import { openDB, IDBPDatabase } from 'idb';

const DB_NAME = 'appligerbe-offline';
const STORE_NAME = 'positions';
const DB_VERSION = 4; // Increment version to force schema update

let dbPromise: Promise<IDBPDatabase> | null = null;
let isUpgrading = false;

export const initDB = async () => {
  // If we already have a database promise and it's not rejected, return it
  if (dbPromise) {
    try {
      await dbPromise;
      return dbPromise;
    } catch (error) {
      // If the existing promise is rejected, clear it and try again
      console.warn('Previous database initialization failed, retrying:', error);
      dbPromise = null;
    }
  }

  // If an upgrade is in progress, wait before trying to open the database
  while (isUpgrading) {
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  try {
    isUpgrading = true;

    // Create a new database promise
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion) {
        // Delete old store if it exists
        if (db.objectStoreNames.contains(STORE_NAME)) {
          db.deleteObjectStore(STORE_NAME);
        }

        // Create new store with composite key
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: ['timestamp', 'driverId']
        });

        // Create indexes
        store.createIndex('synced', 'synced', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('driverId', 'driverId', { unique: false });

        console.log(`Database upgraded from version ${oldVersion} to ${newVersion}`);
      },
      blocked(currentVersion, blockedVersion, event) {
        console.warn(
          `Database upgrade blocked. Current version: ${currentVersion}, blocked version: ${blockedVersion}`,
          event
        );
      },
      blocking(currentVersion, blockedVersion, event) {
        console.warn(
          `Database upgrade blocking. Current version: ${currentVersion}, blocked version: ${blockedVersion}`,
          event
        );
      },
      terminated() {
        console.error('Database connection terminated unexpectedly.');
        dbPromise = null;
        isUpgrading = false;
      },
    });

    // Wait for the database to open successfully
    await dbPromise;
    return dbPromise;
  } catch (error) {
    console.error('Failed to initialize database:', error);
    dbPromise = null;
    throw error;
  } finally {
    isUpgrading = false;
  }
};

export const storePosition = async (position: { lat: number; lng: number; driverId: string }) => {
  try {
    const timestamp = Date.now();
    if (!Number.isFinite(timestamp)) {
      throw new Error('Invalid timestamp');
    }

    if (!position.driverId) {
      throw new Error('Driver ID is required');
    }

    const db = await initDB();
    await db.put(STORE_NAME, {
      ...position,
      timestamp,
      synced: false,
    });
  } catch (error) {
    console.error('Failed to store position:', error);
    throw error;
  }
};

export const getUnsynced = async () => {
  try {
    const db = await initDB();
    const index = db.transaction(STORE_NAME).store.index('synced');
    const unsyncedItems = await index.getAll(0); // Use 0 instead of false for better compatibility
    
    // Filter out any items with invalid data
    return unsyncedItems.filter(item => 
      item &&
      typeof item.timestamp === 'number' && 
      Number.isFinite(item.timestamp) &&
      typeof item.driverId === 'string' &&
      item.driverId.length > 0
    );
  } catch (error) {
    console.error('Failed to get unsynced positions:', error);
    throw error;
  }
};

export const markAsSynced = async (timestamp: number, driverId: string) => {
  try {
    if (!Number.isFinite(timestamp)) {
      throw new Error('Invalid timestamp');
    }

    if (!driverId) {
      throw new Error('Driver ID is required');
    }

    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    const item = await store.get([timestamp, driverId]);
    if (item) {
      item.synced = true;
      await store.put(item);
    }
    await tx.done;
  } catch (error) {
    console.error('Failed to mark position as synced:', error);
    throw error;
  }
};